/**
 * 업무 홈 (전체 화면) — 탭: 홈 · 견적 목록 · 통계 (이전 시스템 main.jsp · 견적/작업/출고현황 · stat01/stat02 자리)
 * 홈: 이번 달 숫자, 진행 단계별 건수, 미수, 출고 예정·지연, 최근 견적, 재질·부자재
 * 견적 목록: 기간·거래처·작성자·재질·두께·상태·검색어 → 엑셀(CSV) 내려받기
 * 통계: 월별(견적·확정·입금·출고) · 거래처별(견적·확정·입금·미수) → 엑셀
 */
import React, { useEffect, useState } from 'react';
import { HomeData, EstRow, EstFilter, Summary, fetchHome, fetchFilters, listEstimates, fetchSummary, createHandQuote, boardRecent, BoardRow, errMsg } from '../utils/api';
import { MATERIAL_SPECS } from '../utils/materialSpecs';
import { MaterialType } from '../types';
import { downloadCsv } from '../utils/csv';
import { jobOf, JOB, d8 } from './WorkBoard';
import { C, inp, btn, chip, th, td, num } from './ui';

const won = (n: number | null | undefined) => Math.round(n || 0).toLocaleString('ko-KR');
const matName = (m: string) => MATERIAL_SPECS[m as MaterialType]?.label ?? (m || '-');
const kstDate = (offsetDays = 0) => new Date(Date.now() + 9 * 3600e3 + offsetDays * 864e5).toISOString().slice(0, 10);
const ym = (s: string) => `${s.slice(0, 4)}.${s.slice(4, 6)}`;
const card: React.CSSProperties = { background: '#fff', borderRadius: 20, padding: 20 };
const STATE_OPTS: [string, string][] = [['', '전체 상태'], ['EST', '견적'], ['CONFIRMED', '확정'], ['WORK', '작업 중'], ['WORKEND', '작업 완료'], ['OUT', '출고'], ['DONE', '설치 완료'], ['UNPAID', '미수 남음'], ['HAND', '수기 견적']];

type Tab = 'home' | 'list' | 'stats';
export interface HomeBoardProps {
  onClose: () => void;
  onOpen: (no: string) => void;        // 견적 열기 (도면이면 도면, 수기면 견적서)
  onNewDrawing: () => void;
  onBoard: (no: string) => void;       // 작업 현황에서 보기
  onNotice?: (id?: number) => void;    // 게시판 (글 번호 = 그 글 바로)
}

const StateTag: React.FC<{ st: EstRow['JOB_STATE']; hand?: boolean }> = ({ st, hand }) => {
  const j = jobOf(st);
  return <span style={{ display: 'inline-flex', gap: 4, whiteSpace: 'nowrap' }}>
    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: j.color, color: '#fff', fontWeight: 600, whiteSpace: 'nowrap' }}>{j.label}</span>
    {hand && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: C.fill, color: C.sub, fontWeight: 600, whiteSpace: 'nowrap' }}>수기</span>}
  </span>;
};

export const HomeBoard: React.FC<HomeBoardProps> = ({ onClose, onOpen, onNewDrawing, onBoard, onNotice }) => {
  const [tab, setTab] = useState<Tab>('home');
  const [newHand, setNewHand] = useState<{ site: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const makeHand = async () => {
    try { const r = await createHandQuote(newHand!.site, null); setNewHand(null); onOpen(r.data.estiNo); } catch (e) { setErr(errMsg(e)); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: C.bg, zIndex: 1400, display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: '#fff', borderBottom: `1px solid ${C.line}`, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <img src="/icon.svg" alt="" width={26} height={26} />
        <b style={{ fontSize: 18, color: C.text, letterSpacing: -0.3, marginRight: 8 }}>업무 홈</b>
        {([['home', '홈'], ['list', '견적 목록'], ['stats', '통계']] as [Tab, string][]).map(([k, l]) => <button key={k} onClick={() => setTab(k)} style={chip(tab === k)}>{l}</button>)}
        <span style={{ flex: 1 }} />
        <a href="/guide/" target="_blank" rel="noopener noreferrer" style={{ ...btn('#757575'), background: 'transparent', textDecoration: 'none' }}>📖 사용 안내</a>
        <button onClick={() => setNewHand({ site: '' })} style={btn('#757575')}>수기 견적 만들기</button>
        <button onClick={onNewDrawing} style={btn()}>도면 그리기</button>
        <button onClick={onClose} style={btn('#757575')}>닫기</button>
      </div>
      {newHand && (
        <div style={{ background: '#fff', borderBottom: `1px solid ${C.line}`, padding: '12px 16px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <b style={{ fontSize: 14 }}>수기 견적</b>
          <span style={{ fontSize: 13, color: C.mute }}>도면 없이 품목만 넣는 견적 (부자재만 파는 경우 등)</span>
          <input autoFocus value={newHand.site} onChange={e => setNewHand({ site: e.target.value })} onKeyDown={e => e.key === 'Enter' && makeHand()} placeholder="현장명 (나중에 바꿀 수 있음)" style={{ ...inp, width: 260 }} />
          <button onClick={makeHand} style={btn()}>만들기</button>
          <button onClick={() => setNewHand(null)} style={btn('#757575')}>취소</button>
          {err && <span style={{ color: C.red, fontSize: 13 }}>⚠ {err}</span>}
        </div>
      )}
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>
          {tab === 'home' && <>{onNotice && <Notices onOpen={onNotice} />}<HomeTab onOpen={onOpen} onBoard={onBoard} onList={() => setTab('list')} /></>}
          {tab === 'list' && <ListTab onOpen={onOpen} />}
          {tab === 'stats' && <StatsTab />}
        </div>
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; sub?: string; color?: string; onClick?: () => void }> = ({ label, value, sub, color, onClick }) => (
  <div onClick={onClick} style={{ ...card, flex: '1 1 180px', cursor: onClick ? 'pointer' : 'default' }}>
    <div style={{ fontSize: 14, color: C.mute, fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 26, fontWeight: 800, color: color ?? C.text, margin: '6px 0 2px', letterSpacing: -0.5 }}>{value}</div>
    {sub && <div style={{ fontSize: 13, color: C.sub }}>{sub}</div>}
  </div>
);

const EstTable: React.FC<{ rows: EstRow[]; onOpen: (no: string) => void; onBoard?: (no: string) => void; due?: string }> = ({ rows, onOpen, onBoard, due }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
    <thead><tr>{['견적번호', '상태', '현장', '거래처', due ? '출고 예정' : '견적일', '금액', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
    <tbody>{rows.map(r => {
      const late = due && r.DUE_DT && r.DUE_DT < due;
      return (
        <tr key={r.ESTI_NO}>
          <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{r.ESTI_NO}</td>
          <td style={td}><StateTag st={r.JOB_STATE} hand={!!r.HAND} /></td>
          <td style={td}>{r.SITE_NM || <span style={{ color: C.mute }}>(현장명 없음)</span>}</td>
          <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.CUST_NM || '-'}</td>
          <td style={{ ...td, whiteSpace: 'nowrap', color: late ? C.red : undefined, fontWeight: late ? 700 : undefined }}>{due ? `${d8(r.DUE_DT)}${late ? ' 지연' : ''}` : d8(r.ESTI_DT)}</td>
          <td style={num}>{r.QUOTED ? won(r.QUOTED) : '-'}</td>
          <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
            {onBoard && !r.HAND && <button onClick={() => onBoard(r.ESTI_NO)} style={{ ...btn('#757575'), height: 28, padding: '0 10px', fontSize: 12, marginRight: 4 }}>작업 현황</button>}
            <button onClick={() => onOpen(r.ESTI_NO)} style={{ ...btn(C.blue, false), height: 28, padding: '0 10px', fontSize: 12 }}>{r.HAND ? '견적서' : '도면'} 열기</button>
          </td>
        </tr>
      );
    })}</tbody>
  </table>
);

/** 최근 공지 (없으면 게시판 바로가기 한 줄) */
const Notices: React.FC<{ onOpen: (id?: number) => void }> = ({ onOpen }) => {
  const [rows, setRows] = useState<Pick<BoardRow, 'POST_ID' | 'PIN_YN' | 'TITLE' | 'REG_DT'>[] | null>(null);
  useEffect(() => { boardRecent().then(r => setRows(r.data.slice(0, 3))).catch(() => setRows([])); }, []);
  if (!rows) return null;
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: '12px 16px', marginBottom: 12, display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <b style={{ fontSize: 14, color: C.text, whiteSpace: 'nowrap', paddingTop: 2 }}>📢 공지</b>
      <div style={{ flex: 1, minWidth: 200 }}>
        {rows.length === 0 ? <span style={{ fontSize: 13, color: C.mute }}>공지가 없습니다</span> : rows.map(r => (
          <div key={r.POST_ID} onClick={() => onOpen(r.POST_ID)} style={{ fontSize: 14, padding: '2px 0', cursor: 'pointer', display: 'flex', gap: 8 }}>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: r.PIN_YN === 'Y' ? 700 : 400 }}>{r.PIN_YN === 'Y' && '📌 '}{r.TITLE}</span>
            <span style={{ color: C.mute, fontSize: 12 }}>{r.REG_DT.slice(5, 10)}</span>
          </div>
        ))}
      </div>
      <button onClick={() => onOpen()} style={{ ...btn('#757575'), height: 30, fontSize: 13 }}>게시판</button>
    </div>
  );
};

const HomeTab: React.FC<{ onOpen: (no: string) => void; onBoard: (no: string) => void; onList: () => void }> = ({ onOpen, onBoard, onList }) => {
  const [d, setD] = useState<HomeData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { fetchHome().then(r => setD(r.data)).catch(e => setErr(errMsg(e))); }, []);
  if (!d) return <div style={{ color: err ? C.red : C.mute, padding: 20 }}>{err ?? '불러오는 중…'}</div>;
  const m = d.month_;
  const active = ['CONFIRMED', 'WORK', 'WORKEND', 'OUT'];
  return (<>
    <h2 style={{ fontSize: 20, margin: '4px 0 12px', color: C.text }}>{d.month.slice(0, 4)}년 {Number(d.month.slice(4))}월</h2>
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <Stat label="견적" value={`${m.n}건`} sub={`${won(m.quoted)}원`} />
      <Stat label="확정" value={`${m.confN}건`} sub={`${won(m.confAmt)}원`} color={C.blue} />
      <Stat label="출고" value={`${m.shipped}건`} sub={`설치 완료 ${m.installed}건`} />
      <Stat label="입금" value={`${won(m.deposit)}원`} sub={`${m.depositN}건`} color={C.green} />
      <Stat label="미수 (전체)" value={`${won(d.receivable.amt)}원`} sub={`${d.receivable.n}건 · 확정된 견적 기준`} color={d.receivable.amt > 0 ? C.red : C.text} />
    </div>
    <div style={{ ...card, marginTop: 12 }}>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>진행 중인 일</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {JOB.filter(j => j.st === null || active.includes(j.st)).map(j => (
          <div key={j.label} style={{ flex: '1 1 120px', background: C.bg, borderRadius: 14, padding: '12px 14px' }}>
            <div style={{ fontSize: 13, color: j.color, fontWeight: 700 }}>{j.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{d.states[j.st ?? 'EST'] ?? 0}<span style={{ fontSize: 13, color: C.mute, fontWeight: 500 }}> 건</span></div>
          </div>
        ))}
      </div>
    </div>
    <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ ...card, flex: '1 1 560px', minWidth: 0, overflow: 'auto' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>출고 예정 · 지연 <span style={{ fontSize: 13, color: C.mute, fontWeight: 500 }}>7일 안 · 아직 안 나간 것</span></div>
        {d.due.length ? <EstTable rows={d.due} onOpen={onOpen} onBoard={onBoard} due={d.today} /> : <div style={{ color: C.mute, fontSize: 14, padding: '8px 0' }}>없습니다</div>}
      </div>
      <div style={{ ...card, flex: '1 1 560px', minWidth: 0, overflow: 'auto' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, display: 'flex' }}>최근 견적
          <button onClick={onList} style={{ ...btn(C.blue, false), height: 28, padding: '0 10px', fontSize: 12, marginLeft: 'auto' }}>견적 목록 →</button></div>
        {d.recent.length ? <EstTable rows={d.recent} onOpen={onOpen} /> : <div style={{ color: C.mute, fontSize: 14, padding: '8px 0' }}>아직 저장한 견적이 없습니다</div>}
      </div>
    </div>
    <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ ...card, flex: '1 1 400px' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>이번 달 재질별</div>
        {d.materials.length ? <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr>{['재질', '두께', '견적', '패널 면적(㎡)'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>{d.materials.map(x => <tr key={`${x.MATERIAL_CD}${x.PANEL_THICK}`}><td style={td}>{matName(x.MATERIAL_CD)}</td><td style={td}>{x.PANEL_THICK}T</td>
            <td style={num}>{x.N}건</td><td style={num}>{x.AREA ? x.AREA.toFixed(2) : '-'}</td></tr>)}</tbody>
        </table> : <div style={{ color: C.mute, fontSize: 14 }}>없습니다</div>}
      </div>
      <div style={{ ...card, flex: '1 1 400px' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>이번 달 문 · 부자재 <span style={{ fontSize: 13, color: C.mute, fontWeight: 500 }}>견적서 기준</span></div>
        {d.subitems.length ? <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr>{['품목', '수량', '금액'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>{d.subitems.map(x => <tr key={x.ITEM_NM}><td style={td}>{x.ITEM_NM}</td><td style={num}>{x.QTY}{x.UNIT || ''}</td><td style={num}>{won(x.AMT)}</td></tr>)}</tbody>
        </table> : <div style={{ color: C.mute, fontSize: 14 }}>없습니다</div>}
      </div>
    </div>
  </>);
};

const ListTab: React.FC<{ onOpen: (no: string) => void }> = ({ onOpen }) => {
  const [f, setF] = useState<EstFilter>({ from: kstDate(-90), to: kstDate(), cust: '', user: '', mat: '', thick: '', state: '', q: '' });
  const [opts, setOpts] = useState<{ customers: { CUST_ID: number; CUST_NM: string }[]; users: { USER_ID: string; USER_NM: string }[] }>({ customers: [], users: [] });
  const [rows, setRows] = useState<EstRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = (x = f) => listEstimates(x).then(r => { setRows(r.data); setErr(null); }).catch(e => setErr(errMsg(e)));
  useEffect(() => { fetchFilters().then(r => setOpts(r.data)).catch(() => {}); load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k: keyof EstFilter) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const sel: React.CSSProperties = { ...inp, minWidth: 110 };
  const sum = (k: 'QUOTED' | 'BILLED' | 'PAID') => (rows || []).reduce((a, r) => a + r[k], 0);
  const excel = () => rows && downloadCsv(`견적목록_${f.from}_${f.to}`,
    ['견적번호', '견적일', '구분', '상태', '거래처', '현장', '재질', '두께', '가로', '세로', '견적액', '청구액', '입금', '미수', '작업일', '출고 예정일', '최근 배차일', '작성자'],
    rows.map(r => [r.ESTI_NO, d8(r.ESTI_DT), r.HAND ? '수기' : '도면', jobOf(r.JOB_STATE).label, r.CUST_NM, r.SITE_NM, r.HAND ? '' : matName(r.MATERIAL_CD), r.HAND ? '' : r.PANEL_THICK,
      r.HAND ? '' : r.HORIZONTAL, r.HAND ? '' : r.VERTICAL, r.QUOTED, r.BILLED, r.PAID, r.BILLED - r.PAID, d8(r.WORK_DT), d8(r.DUE_DT), d8(r.SHIP_DT), r.USER_NM || r.USER_ID]));
  return (<div style={card}>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
      <input type="date" value={f.from} onChange={set('from')} style={inp} /><span style={{ color: C.mute }}>~</span><input type="date" value={f.to} onChange={set('to')} style={inp} />
      <select value={f.cust} onChange={set('cust')} style={sel}><option value="">전체 거래처</option>{opts.customers.map(c => <option key={c.CUST_ID} value={c.CUST_ID}>{c.CUST_NM}</option>)}</select>
      <select value={f.user} onChange={set('user')} style={sel}><option value="">전체 작성자</option>{opts.users.map(u => <option key={u.USER_ID} value={u.USER_ID}>{u.USER_NM}</option>)}</select>
      <select value={f.mat} onChange={set('mat')} style={sel}><option value="">전체 재질</option>{Object.keys(MATERIAL_SPECS).map(m => <option key={m} value={m}>{matName(m)}</option>)}</select>
      <select value={f.thick} onChange={set('thick')} style={sel}><option value="">전체 두께</option>{[75, 100, 125, 150].map(t => <option key={t} value={t}>{t}T</option>)}</select>
      <select value={f.state} onChange={set('state')} style={sel}>{STATE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      <input value={f.q} onChange={set('q')} onKeyDown={e => e.key === 'Enter' && load()} placeholder="견적번호·현장·거래처" style={{ ...inp, width: 180 }} />
      <button onClick={() => load()} style={btn()}>검색</button>
      <button onClick={excel} disabled={!rows?.length} style={{ ...btn('#757575'), marginLeft: 'auto', opacity: rows?.length ? 1 : 0.4 }}>엑셀 내려받기</button>
    </div>
    {err && <div style={{ color: C.red, fontSize: 13, marginBottom: 8 }}>⚠ {err}</div>}
    {rows && <div style={{ fontSize: 13, color: C.sub, marginBottom: 8 }}>
      {rows.length}건 · 견적액 <b>{won(sum('QUOTED'))}</b> · 청구 <b>{won(sum('BILLED'))}</b> · 입금 <b style={{ color: C.green }}>{won(sum('PAID'))}</b> · 미수 <b style={{ color: C.red }}>{won(sum('BILLED') - sum('PAID'))}</b>
      {rows.length >= 1000 && <span style={{ color: C.orange }}> — 1000건까지만 보입니다. 기간을 줄여 주세요</span>}</div>}
    <div style={{ overflow: 'auto' }}>
      {rows === null ? <div style={{ color: C.mute }}>불러오는 중…</div> : rows.length === 0 ? <div style={{ color: C.mute, padding: '8px 0' }}>조건에 맞는 견적이 없습니다</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr>{['견적번호', '견적일', '상태', '거래처', '현장', '재질·규격', '견적액', '입금', '미수', '출고 예정', '작성자', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.ESTI_NO}>
              <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{r.ESTI_NO}</td>
              <td style={td}>{d8(r.ESTI_DT)}</td>
              <td style={td}><StateTag st={r.JOB_STATE} hand={!!r.HAND} /></td>
              <td style={td}>{r.CUST_NM || '-'}</td>
              <td style={td}>{r.SITE_NM || <span style={{ color: C.mute }}>-</span>}</td>
              <td style={td}>{r.HAND ? '-' : `${matName(r.MATERIAL_CD)} ${r.PANEL_THICK}T · ${r.HORIZONTAL}×${r.VERTICAL}`}</td>
              <td style={num}>{r.QUOTED ? won(r.QUOTED) : '-'}</td>
              <td style={{ ...num, color: C.green }}>{r.PAID ? won(r.PAID) : '-'}</td>
              <td style={{ ...num, color: r.BILLED - r.PAID > 0 ? C.red : C.mute, fontWeight: r.BILLED - r.PAID > 0 ? 700 : 400 }}>{r.BILLED ? won(r.BILLED - r.PAID) : '-'}</td>
              <td style={td}>{d8(r.DUE_DT)}</td>
              <td style={td}>{r.USER_NM || r.USER_ID}</td>
              <td style={{ ...td, textAlign: 'right' }}><button onClick={() => onOpen(r.ESTI_NO)} style={{ ...btn(C.blue, false), height: 28, padding: '0 10px', fontSize: 12 }}>열기</button></td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  </div>);
};

const StatsTab: React.FC = () => {
  const year = kstDate().slice(0, 4);
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(kstDate());
  const [d, setD] = useState<Summary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = () => fetchSummary(from, to).then(r => { setD(r.data); setErr(null); }).catch(e => setErr(errMsg(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const smallBtn: React.CSSProperties = { ...btn('#757575'), height: 30, padding: '0 10px', fontSize: 12, marginLeft: 'auto' };
  const tot = (k: keyof Summary['months'][number]) => (d?.months || []).reduce((a, m) => a + Number(m[k]), 0);
  return (<>
    <div style={{ ...card, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <b style={{ marginRight: 4 }}>기간</b>
      <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inp} /><span style={{ color: C.mute }}>~</span>
      <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inp} />
      <button onClick={load} style={btn()}>조회</button>
      <span style={{ fontSize: 12, color: C.mute }}>견적·확정은 견적일, 입금은 입금일, 출고는 배차 출고일 기준</span>
      {err && <span style={{ color: C.red, fontSize: 13 }}>⚠ {err}</span>}
    </div>
    {d && <>
      <div style={{ ...card, marginTop: 12, overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}><b style={{ fontSize: 16 }}>월별 견적 · 출고 현황</b>
          <button style={smallBtn} onClick={() => downloadCsv(`월별현황_${from}_${to}`, ['월', '견적 건수', '견적액', '확정 건수', '확정액', '입금', '출고 건수', '운임'],
            d.months.map(m => [ym(m.YM), m.N, m.QUOTED, m.CONF_N, m.CONF_AMT, m.DEPOSIT, m.SHIP_N, m.FEE]))}>엑셀 내려받기</button></div>
        {d.months.length === 0 ? <div style={{ color: C.mute }}>기간 안에 자료가 없습니다</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['월', '견적', '견적액', '확정', '확정액', '입금', '출고', '운임'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {d.months.map(m => <tr key={m.YM}><td style={td}>{ym(m.YM)}</td><td style={num}>{m.N}</td><td style={num}>{won(m.QUOTED)}</td><td style={num}>{m.CONF_N}</td>
                <td style={num}>{won(m.CONF_AMT)}</td><td style={{ ...num, color: C.green }}>{won(m.DEPOSIT)}</td><td style={num}>{m.SHIP_N}</td><td style={num}>{won(m.FEE)}</td></tr>)}
              <tr style={{ fontWeight: 700 }}><td style={td}>합계</td><td style={num}>{tot('N')}</td><td style={num}>{won(tot('QUOTED'))}</td><td style={num}>{tot('CONF_N')}</td>
                <td style={num}>{won(tot('CONF_AMT'))}</td><td style={{ ...num, color: C.green }}>{won(tot('DEPOSIT'))}</td><td style={num}>{tot('SHIP_N')}</td><td style={num}>{won(tot('FEE'))}</td></tr>
            </tbody>
          </table>
        )}
      </div>
      <div style={{ ...card, marginTop: 12, overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}><b style={{ fontSize: 16 }}>거래처별 현황</b>
          <button style={smallBtn} onClick={() => downloadCsv(`거래처별현황_${from}_${to}`, ['거래처', '견적 건수', '견적액', '확정 건수', '청구액', '입금', '미수'],
            d.customers.map(c => [c.CUST_NM, c.N, c.QUOTED, c.CONF_N, c.BILLED, c.PAID, c.BILLED - c.PAID]))}>엑셀 내려받기</button></div>
        {d.customers.length === 0 ? <div style={{ color: C.mute }}>기간 안에 자료가 없습니다</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['거래처', '견적', '견적액', '확정', '청구액', '입금', '미수'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>{d.customers.map(c => <tr key={c.CUST_ID ?? 0}><td style={td}>{c.CUST_NM}</td><td style={num}>{c.N}</td><td style={num}>{won(c.QUOTED)}</td><td style={num}>{c.CONF_N}</td>
              <td style={num}>{won(c.BILLED)}</td><td style={{ ...num, color: C.green }}>{won(c.PAID)}</td>
              <td style={{ ...num, color: c.BILLED - c.PAID > 0 ? C.red : C.mute, fontWeight: 700 }}>{won(c.BILLED - c.PAID)}</td></tr>)}</tbody>
          </table>
        )}
        <div style={{ fontSize: 12, color: C.mute, marginTop: 8 }}>청구액·미수는 확정된 견적만 (견적액은 확정 전 포함)</div>
      </div>
    </>}
  </>);
};

export default HomeBoard;
